import {
  Fragment,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react'

import {
  Dialog,
  DialogPanel,
  Transition,
  TransitionChild,
} from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { cn } from '@/lib/utils'

type Size = 's' | 'm' | 'md' | 'l' | 'xl'

interface ModalProps {
  state: boolean
  close: () => void
  size?: Size
}

const sizeClasses = {
  s: 'lg:w-1/3',
  m: 'lg:w-1/2',
  md: 'lg:w-3/5',
  l: 'lg:w-4/5',
  xl: 'min-h-[110vh] min-w-[90vw]',
}

export default function Modal({
  state,
  close,
  children,
  size = 'm',
}: ModalProps & PropsWithChildren) {
  const [isVisible, setIsVisible] = useState(state)

  useEffect(() => {
    setIsVisible(state)
  }, [state])

  const handleClose = useCallback(() => {
    setIsVisible(false)
    setTimeout(() => {
      close()
    }, 300)
  }, [close])

  return (
    <Transition appear as={Fragment} show={isVisible}>
      <Dialog as="div" className="fixed inset-0 z-40" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" />
        </TransitionChild>

        <div className="fixed inset-0 z-30 overflow-y-auto scrollbar-none">
          <div className="flex min-h-full items-center justify-center p-4 text-center lg:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-500 transform"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-300 transform"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                className={cn(
                  'relative transform overflow-y-visible rounded-lg bg-gray-100 px-4 pb-4',
                  `pt-5 text-left shadow-xl transition-all lg:my-8 ${sizeClasses[size]}`,
                  'lg:h-1/2 lg:max-w-7xl lg:p-6'
                )}
              >
                <div className="fixed right-0 top-2 p-3 lg:block">
                  <button
                    className="rounded-md bg-gray-100 text-gray-400 focus:outline-none hover:text-gray-500"
                    onClick={handleClose}
                    type="button"
                  >
                    <span className="sr-only">Cerrar</span>
                    <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                  </button>
                </div>
                <div className="pt-5">{children}</div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
